import { Card, CardContent, CardHeader, CardTitle } from '../../../../../components/ui/card';
import { Circle, Package, AlertCircle, Plane, Globe, Home, Building2 } from 'lucide-react';
import type { TraceItem } from '../../../../../infrastructure/OMSClient';
import { translateStatus } from '../../utils/translate';

interface DeliveryTraceProps {
  trackingNo: string;
  trackingHistory: TraceItem[];
  orderId: string;
}

export function DeliveryTrace({ trackingNo, trackingHistory, orderId }: DeliveryTraceProps) {
  if (!trackingHistory || trackingHistory.length === 0) {
    return (
      <div className="space-y-4">
        {/* Tracking Number Card */}
        <Card>
          <CardHeader className="py-2">
            <div className="flex items-center gap-2">
              <CardTitle>Tracking Information</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="py-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-sm text-muted-foreground">Order ID: </span>
                <span className="text-lg font-medium">{orderId}</span>
              </div>
              <div>
                <span className="text-sm text-muted-foreground">Tracking Number: </span>
                <span className="text-lg font-medium">{trackingNo}</span>
              </div>
              <div>
                <span className="text-sm text-muted-foreground">Status: </span>
                <span className="text-lg font-medium">No tracking information available</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Empty State Message */}
        <Card>
          <CardContent className="py-8">
            <div className="flex flex-col items-center justify-center text-center space-y-2">
              <AlertCircle className="h-8 w-8 text-muted-foreground" />
              <h3 className="text-lg font-medium">No Tracking History</h3>
              <p className="text-sm text-muted-foreground">
                Tracking information for this package is not yet available. Please check back later.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Filter out parent events and sort by eventTime in descending order
  const sortedEvents = trackingHistory
    .filter((event) => event.eventTime !== null) // Only show events with timestamps
    .sort((a, b) => {
      const timeA = new Date(a.eventTime!).getTime();
      const timeB = new Date(b.eventTime!).getTime();
      return timeB - timeA;
    });

  // Get the current status (first event with a status)
  const currentStatus = sortedEvents.find((event) => event.status !== null);

  const getStatusIcon = (status: string | null) => {
    if (!status) {
      return <Circle className="h-6 w-6 text-gray-300" />;
    }

    if (status.startsWith('D')) {
      return <Home className="h-6 w-6 text-green-500" />;
    } else if (status.startsWith('I')) {
      switch (status) {
        case 'I00':
          return <Package className="h-6 w-6 text-blue-500" />;
        case 'I01':
          return <Plane className="h-6 w-6 text-blue-500" />;
        case 'I02':
          return <Globe className="h-6 w-6 text-orange-500" />;
        case 'I04':
          return <Building2 className="h-6 w-6 text-purple-500" />;
        default:
          return <Circle className="h-6 w-6 text-gray-300" />;
      }
    }
    return <Circle className="h-6 w-6 text-gray-300" />;
  };

  return (
    <div className="space-y-4">
      {/* Tracking Number Card */}
      <Card>
        <CardHeader className="py-2">
          <div className="flex items-center gap-2">
            <CardTitle>Tracking Information</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="py-2">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <span className="text-sm text-muted-foreground">Order ID: </span>
              <span className="text-lg font-medium" style={{ color: 'blue', fontWeight: 'bold' }}>
                {orderId}
              </span>
            </div>
            <div>
              <span className="text-sm text-muted-foreground">Tracking Number: </span>
              <span className="text-lg font-medium" style={{ color: 'blue', fontWeight: 'bold' }}>
                {trackingNo}
              </span>
            </div>
            <div>
              <span className="text-sm text-muted-foreground">Current Status: </span>
              <span className="text-lg" style={{ color: 'purple', fontWeight: 'bold' }}>
                {currentStatus ? translateStatus(currentStatus.description) : 'Unknown'}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tracking History Timeline */}
      <Card className="pt-3">
        <CardHeader className="py-1">
          <div className="flex items-center gap-2">
            <CardTitle>Tracking History</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="py-2">
          <div className="space-y-4 m-1">
            {sortedEvents.map((event, index) => (
              <div key={index} className="flex mt-3">
                <div className="flex flex-col items-center">
                  {getStatusIcon(event.status)}
                  {index < sortedEvents.length - 1 && <div className="w-0.5 h-full bg-gray-200" />}
                </div>
                <div className="flex-1">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium">{translateStatus(event.description)}</p>
                    </div>
                    <p className="text-sm text-muted-foreground">{event.eventTime}</p>
                  </div>
                </div>
                <hr></hr>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
